import { useEffect, useMemo, useState } from 'react';
import { Loader2, Building2, ChevronDown, BarChart2, Users, Network, LayoutGrid } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import FiscalYearSelector from '../components/FiscalYearSelector';
import TopAgencyTable from '../components/top-position/TopAgencyTable';
import CoproReportingTabs from '../components/top-position/CoproReportingTabs';
import AgencyStatistics from '../components/top-position/AgencyStatistics';
import AgencyNegotiatorPerformance from '../components/reporting-agence/AgencyNegotiatorPerformance';
import SyntheseAgence from '../components/reporting-agence/SyntheseAgence';
import { useAgencyStats } from '../hooks/useAgencyStats';
import { useResponsibleAgencies } from '../hooks/useResponsibleAgencies';
import { Agency } from '../types/agency';

type ActiveTab = 'agencies' | 'copro' | 'statistics' | 'negotiators' | 'synthese';

export default function ReportingAgence() {
  const { user } = useAuth();
  const [fiscalYear, setFiscalYear] = useState<number>(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return month <= 4 ? today.getFullYear() - 1 : today.getFullYear();
  });
  const [period, setPeriod] = useState<'year' | 'quarter' | 'month'>('year');
  const [selectedPeriod, setSelectedPeriod] = useState('1');
  const [activeTab, setActiveTab] = useState<ActiveTab>('agencies');
  const [selectedAgencyForStats, setSelectedAgencyForStats] = useState<string>('ALL');

  const { agencies, loading: agenciesLoading, error: agenciesError, loadData } = useAgencyStats();
  const { agencies: responsibleAgencies, loading: responsibleLoading, error: responsibleError, loadUserAgencies } = useResponsibleAgencies();

  useEffect(() => {
    const { startDate, endDate } = getDateRange();
    loadData(startDate, endDate);
  }, [fiscalYear, period, selectedPeriod]);

  useEffect(() => {
    if ((user?.role === 'responsable' || user?.role === 'direction') && user.id && !agenciesLoading && responsibleAgencies.length === 0) {
      loadUserAgencies(user.id);
    }
  }, [agenciesLoading, user?.role, user?.id, responsibleAgencies.length]);

  useEffect(() => {
    if ((user?.role === 'responsable' || user?.role === 'direction') && user.id) {
      loadUserAgencies(user.id);
    }
  }, [user?.role, user?.id]);

  const getDateRange = () => {
    let startDate: string;
    let endDate: string;

    if (period === 'year') {
      startDate = `${fiscalYear}-04-01`;
      endDate = `${fiscalYear + 1}-03-31`;
    } else if (period === 'quarter') {
      const quarterMap = {
        '1': { start: '04', end: '06', year: fiscalYear },
        '2': { start: '07', end: '09', year: fiscalYear },
        '3': { start: '10', end: '12', year: fiscalYear },
        '4': { start: '01', end: '03', year: fiscalYear + 1 }
      } as const;
      const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap] ?? quarterMap['1'];
      startDate = `${quarter.year}-${quarter.start}-01`;
      endDate = `${quarter.year}-${quarter.end}-${new Date(quarter.year, parseInt(quarter.end), 0).getDate()}`;
    } else {
      const month = selectedPeriod.padStart(2, '0');
      const year = parseInt(month) <= 3 ? fiscalYear + 1 : fiscalYear;
      startDate = `${year}-${month}-01`;
      endDate = `${year}-${month}-${new Date(year, parseInt(month), 0).getDate()}`;
    }

    return { startDate, endDate };
  };

  const handlePeriodChange = (year: number, newPeriod: 'year' | 'quarter' | 'month', newSelectedPeriod: string) => {
    setFiscalYear(year);
    if (newPeriod !== period) {
      if (newPeriod === 'month') {
        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const defaultMonth = currentMonth >= 4 ? String(currentMonth).padStart(2, '0') : '04';
        setPeriod(newPeriod);
        setSelectedPeriod(defaultMonth);
      } else if (newPeriod === 'quarter') {
        setPeriod(newPeriod);
        setSelectedPeriod('1');
      } else {
        setPeriod(newPeriod);
        setSelectedPeriod('1');
      }
    } else {
      setPeriod(newPeriod);
      setSelectedPeriod(newSelectedPeriod);
    }
  };

  const visibleAgencies: Agency[] = useMemo(() => {
    if (!user) return [];
    if (user.role === 'admin') return agencies;
    if (user.role === 'admin' && user.agency === 'Siège') return agencies;

    const parseUserAgencies = (agencyStr: string | undefined) =>
      (agencyStr || '').split(',').map(s => s.trim()).filter(Boolean);

    if (user.role === 'user') {
      const userAgencies = parseUserAgencies(user.agency);
      if (userAgencies.length === 0) return [];
      const allowed = new Set(userAgencies);
      return agencies.filter(a => allowed.has(a.name));
    }

    if (user.role === 'responsable' || user.role === 'direction') {
      const assigned = new Set(responsibleAgencies);
      let allowed = assigned;
      if (allowed.size === 0) {
        const userAgencies = parseUserAgencies(user.agency);
        allowed = new Set(userAgencies);
      }
      if (allowed.size === 0) return [];
      return agencies.filter(a => allowed.has(a.name));
    }

    return [];
  }, [agencies, user, responsibleAgencies]);

  useEffect(() => {
    if (selectedAgencyForStats !== 'ALL' && !visibleAgencies.some(a => a.name === selectedAgencyForStats)) {
      setSelectedAgencyForStats('ALL');
    }
    if (visibleAgencies.length === 1) {
      setSelectedAgencyForStats(visibleAgencies[0].name);
    }
  }, [visibleAgencies, selectedAgencyForStats]);

  const loading = agenciesLoading || ((user?.role === 'responsable' || user?.role === 'direction') && responsibleLoading);
  const error = agenciesError || ((user?.role === 'responsable' || user?.role === 'direction') && responsibleError);

  const canSeeCopro = !!user
    && ((user.role === 'user' || user.role === 'responsable' || user.role === 'direction')
        && visibleAgencies.some(a => a.company === 'KA'))
    || (user.role === 'admin' && user.agency === 'SIEGE');

  const agenciesForStats = useMemo(() => {
    if (selectedAgencyForStats === 'ALL') return visibleAgencies;
    return visibleAgencies.filter(a => a.name === selectedAgencyForStats);
  }, [visibleAgencies, selectedAgencyForStats]);

  const tabs = [
    { id: 'agencies'    as ActiveTab, label: 'Par Agence',   Icon: Building2   },
    ...(canSeeCopro ? [{ id: 'copro' as ActiveTab, label: 'Copro', Icon: Network }] : []),
    { id: 'statistics'  as ActiveTab, label: 'Statistiques', Icon: BarChart2   },
    { id: 'negotiators' as ActiveTab, label: 'Négociateurs', Icon: Users       },
    { id: 'synthese'    as ActiveTab, label: 'Synthèse',     Icon: LayoutGrid  },
  ];

  /* ── loading ── */
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500">Chargement du reporting…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-violet-500/10 rounded-full translate-y-1/2 pointer-events-none" />

        {/* Title row */}
        <div className="relative px-7 pt-6 pb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <Building2 size={22} className="text-white/80" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Reporting Agence</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="text-indigo-300 font-semibold">{visibleAgencies.length}</span>
                {' '}agence{visibleAgencies.length !== 1 ? 's' : ''} dans votre périmètre
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <FiscalYearSelector
              value={fiscalYear}
              period={period}
              selectedPeriod={selectedPeriod}
              onChange={handlePeriodChange}
              variant="dark"
            />
          </div>
        </div>

        {/* Tab bar */}
        <div className="relative px-7 pb-4 flex items-center gap-1 border-t border-white/10 pt-3">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeTab === id
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-white/55 hover:text-white hover:bg-white/10'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ── No agencies warning ── */}
      {!error && (user?.role === 'responsable' || user?.role === 'direction') && responsibleAgencies.length === 0 && visibleAgencies.length === 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm">
          Aucune agence assignée. Veuillez contacter un administrateur pour vous attribuer des agences.
        </div>
      )}

      {/* ── Agency selector (statistics tab only) ── */}
      {activeTab === 'statistics' && visibleAgencies.length > 1 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-3 flex items-center gap-3">
          <Building2 size={15} className="text-slate-400 shrink-0" />
          <span className="text-xs font-medium text-slate-500 shrink-0">Agence</span>
          <div className="relative">
            <select
              value={selectedAgencyForStats}
              onChange={(e) => setSelectedAgencyForStats(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors cursor-pointer"
            >
              <option value="ALL">Toutes les agences</option>
              {visibleAgencies.map(a => (
                <option key={a.name} value={a.name}>{a.name}</option>
              ))}
            </select>
            <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>
      )}

      {/* ── Tab content ── */}
      {activeTab === 'agencies' && visibleAgencies.length > 0 && (
        <TopAgencyTable agencies={visibleAgencies} period={period} />
      )}

      {activeTab === 'agencies' && visibleAgencies.length === 0 && !error && (
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-600 text-sm">
          Aucune donnée à afficher pour votre périmètre d'agences.
        </div>
      )}

      {activeTab === 'copro' && canSeeCopro && (
        <CoproReportingTabs
          period={period}
          selectedPeriod={selectedPeriod}
          fiscalYear={fiscalYear}
        />
      )}

      {activeTab === 'statistics' && visibleAgencies.length > 0 && (
        <AgencyStatistics
          agencies={agenciesForStats}
          startDate={getDateRange().startDate}
          endDate={getDateRange().endDate}
        />
      )}

      {activeTab === 'negotiators' && visibleAgencies.length > 0 && (
        <AgencyNegotiatorPerformance
          visibleAgencies={visibleAgencies.map(a => a.name)}
          fiscalYear={fiscalYear}
          period={period}
          selectedPeriod={selectedPeriod}
        />
      )}

      {activeTab === 'synthese' && (
        <SyntheseAgence
          agencies={visibleAgencies}
          fiscalYear={fiscalYear}
        />
      )}
    </div>
  );
}
